import { useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../services/axiosInstance';
import { getFeatureCopy } from '../components/PlanGate/featureCopy';

interface PlanGateResult {
  allowed: boolean;
  locked: boolean;
  featureName: string;
  requiredPlan: string;
  message: string;
}

// Nombre y plan requerido salen del catálogo único de textos por feature
// (components/PlanGate/featureCopy.ts), alineado con FEATURE_GATES del backend.

/**
 * Fuente de verdad: GET /api/v2/account/plan-features (spec 007, D8).
 * El claim JWT `planFeatures` queda stale ante upgrades de plan (hasta
 * re-login), así que solo se usa como fallback si el endpoint falla.
 * Cache a nivel de módulo (TTL 5 min) compartido entre todos los consumidores.
 */
const CACHE_TTL_MS = 5 * 60_000;
let cachedFeatures: Record<string, boolean> | null = null;
let cacheExpiresAt = 0;
let inflight: Promise<Record<string, boolean> | null> | null = null;

async function fetchPlanFeatures(): Promise<Record<string, boolean> | null> {
  if (cachedFeatures && cacheExpiresAt > Date.now()) return cachedFeatures;
  if (inflight) return inflight;

  inflight = axiosInstance
    .get('/api/v2/account/plan-features')
    .then((res) => {
      const features = res.data?.data?.features ?? null;
      if (features) {
        cachedFeatures = features;
        cacheExpiresAt = Date.now() + CACHE_TTL_MS;
      }
      return features;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Permite invalidar el cache (p.ej. tras un upgrade de plan). */
export function invalidatePlanGateCache(): void {
  cachedFeatures = null;
  cacheExpiresAt = 0;
}

export const PLAN_FEATURES_CHANGED = 'plan-features-changed';

/**
 * Mapa completo feature→incluida (cache compartido con usePlanGate; fallback
 * al claim del JWT si el endpoint falla). Lo usa el menú para los candados.
 */
export async function getPlanFeatures(): Promise<Record<string, boolean> | null> {
  return (await fetchPlanFeatures()) ?? featuresFromJwt();
}

/**
 * Tras comprar un plan: invalida el cache y avisa a todos los usePlanGate
 * montados para que vuelvan a consultar (la pantalla bloqueada se abre sola).
 */
export function refreshPlanGate(): void {
  invalidatePlanGateCache();
  window.dispatchEvent(new Event(PLAN_FEATURES_CHANGED));
}

function featuresFromJwt(): Record<string, boolean> | null {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    const decoded: any = jwtDecode(token);
    return decoded.planFeatures ? JSON.parse(decoded.planFeatures) : null;
  } catch {
    return null;
  }
}

function buildResult(featureCode: string, features: Record<string, boolean> | null): PlanGateResult {
  // Sin información (endpoint caído y sin claim): no bloquear desde el
  // frontend — el backend es quien hace cumplir.
  const allowed = features && featureCode in features ? !!features[featureCode] : true;
  const copy = getFeatureCopy(featureCode);
  return {
    allowed,
    locked: !allowed,
    featureName: copy.name,
    requiredPlan: copy.requiredPlan,
    message: allowed ? '' : `Disponible en el plan ${copy.requiredPlan}`,
  };
}

export function usePlanGate(featureCode: string): PlanGateResult {
  // Render inicial: cache fresco si existe, si no el claim del JWT.
  const initial = useMemo(
    () => buildResult(featureCode, cachedFeatures && cacheExpiresAt > Date.now() ? cachedFeatures : featuresFromJwt()),
    [featureCode],
  );
  const [result, setResult] = useState<PlanGateResult>(initial);

  useEffect(() => {
    let active = true;
    const load = () =>
      fetchPlanFeatures().then((features) => {
        if (active && features) setResult(buildResult(featureCode, features));
      });
    load();
    window.addEventListener(PLAN_FEATURES_CHANGED, load);
    return () => {
      active = false;
      window.removeEventListener(PLAN_FEATURES_CHANGED, load);
    };
  }, [featureCode]);

  return result;
}
